import {
  FormControl,
  Grid,
  Box,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { useEffect, useState } from 'react';

import { AiTwotoneEdit } from 'react-icons/ai';
import { useToastrVanila } from '../../../../../hooks/util/useToastrVanila';
import { useTimeRecordService } from '../../../../../hooks/services/useTimeRecordService';
import { useCalculationDate } from '../../../../../hooks/services/useCalculationDate';
import ITimeRecord from '../../../../../interfaces/ITimeRecord';
import ICalculationDate from '../../../../../interfaces/ICalculationDate';
import moment from 'moment';
import IWizardStep from '../../../../../interfaces/IWizardStep';
import { v4 } from 'uuid';
import { Container } from './TimeRecordList.styles';
import PageTitle from '../../../../../components/page-title/PageTitle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface TimeRecordsListProps extends IWizardStep {}

const TimeRecordList = (props: TimeRecordsListProps) => {
  const [timeRecords, setTimeRecords] = useState<ITimeRecord[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateSelected, setDateSelected] = useState(props.medicalCertificate.calculationDate);
  const [calculationDates, setCalculationDates] = useState<ICalculationDate[]>();
  const [calculationDate, setCalculationDate] = useState<ICalculationDate>();
  const [disable, setDisable] = useState<boolean | null | undefined>(props.medicalCertificate.allowsChange);

  const request = useTimeRecordService();
  const requestCalculationDate = useCalculationDate();
  const toastr = useToastrVanila();

  moment.locale();

  localStorage.setItem('step', '3');

  const handleChangeDate = async (event: SelectChangeEvent) => {
    const calculationDateSelected = calculationDates?.find(
      value => event.target.value === `${value?.initialDate ?? ' '} ${value?.finalDate ?? ' '}`,
    );

    props.medicalCertificate.calculationDate = event.target.value;
    props.medicalCertificate.allowsChange = calculationDateSelected?.allowsChange;

    setCalculationDate(calculationDateSelected);
    setDisable(calculationDateSelected?.allowsChange);
    setDateSelected(event.target.value);
    if (event.target.value) {
      list(event.target.value);
    }
  };

  const generateDayIntervalBetweenDates = async (endDate: string) => {
    const endDay = endDate.slice(8, 10);
    const month = endDate.slice(5, 7);
    const year = endDate.slice(0, 4);

    const list = [];

    for (let i = 1; i <= Number(endDay); i++) {
      list.push({ id: 'i', value: `${year}-${month}-${i < 10 ? '0' : ''}${i}` });
    }

    return list;
  };

  const list = async (date: string) => {
    setLoading(true);
    const newDate = date.split(' ');

    await request
      .listByEmployeeAndDateTimeRecords(newDate[0], newDate[1], props.medicalCertificate.employeeId)
      .then(async results => {
        const rows: ITimeRecord[] = [];

        const dateLists = generateDayIntervalBetweenDates(newDate[1]);

        let i = 0;
        (await dateLists).forEach(dateList => {
          let pointsList: string[] = [];
          let status: string = '';

          results.forEach((result: ITimeRecord) => {
            if (moment(result.date).format('yyyy-MM-DD') === dateList.value) {
              status = result.status ?? '';
              pointsList.push(moment(result.points).format('HH:mm'));
            }
          });

          rows[i] = {
            id: dateList.id,
            date: dateList.value,
            points: pointsList,
            status: status,
          };

          i++;
        });

        setTimeRecords(null);
        setTimeRecords(rows);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const listPointControlCalculationDate = async () => {
    setLoading(true);
    await requestCalculationDate
      .listCalculationDate(props.medicalCertificate.employeeId)
      .then(result => {
        setCalculationDates(result);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (props.medicalCertificate.employeeId) {
      listPointControlCalculationDate();
    }

    if (props.medicalCertificate.calculationDate) {
      setDisable(
        props.medicalCertificate.allowsChange === null
          ? calculationDate?.allowsChange
          : props.medicalCertificate.allowsChange,
      );

      if (
        props.medicalCertificate.calculationDate &&
        props.medicalCertificate.calculationDate !== 'undefined undefined'
      ) {
        list(props.medicalCertificate.calculationDate);
      }
    }
  }, []);

  return (
    <Container>
      <PageTitle title={`ACERTOS DO COLABORADOR`}></PageTitle>

      <Box sx={{ marginTop: 2 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            <Typography>Filtros</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item md={2} sm={12} xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="select-dates">Data de Calculo</InputLabel>
                  <Select
                    labelId="select-dates"
                    label="Data de calculo"
                    onChange={handleChangeDate}
                    value={dateSelected}
                    sx={{ width: '260px', height: '52px' }}
                  >
                    {calculationDates?.map(date => (
                      <MenuItem key={date?.id} value={`${date?.initialDate} ${date?.finalDate}`}>
                        {`${moment(date?.initialDate).format('DD/MM/yyyy')} até ${moment(date?.finalDate).format(
                          'DD/MM/yyyy',
                        )}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>

      <TableContainer component={Paper} sx={{ marginTop: '20px' }}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Dias</TableCell>
              <TableCell>Horário</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {timeRecords ? (
              timeRecords.map(timeRecords => {
                return (
                  <TableRow key={timeRecords?.date}>
                    <TableCell>
                      {moment(timeRecords?.date).format('DD/MM')}
                      <br />
                      {moment(timeRecords?.date).format('dddd')}
                    </TableCell>
                    <TableCell>
                      {timeRecords.points &&
                        timeRecords.points.map(point => {
                          return (
                            <div key={v4()}>
                              <label>{point}</label>
                              <br />
                            </div>
                          );
                        })}
                    </TableCell>
                    <TableCell>{timeRecords?.status}</TableCell>
                    <TableCell>
                      <Tooltip title="Editar" placement="top">
                        <IconButton
                          disabled={!disable}
                          onClick={async () => {
                            props.medicalCertificate.tempDate = timeRecords.date;
                            await props.setStep(props.step + 1, { ...props.medicalCertificate });
                            setLoading(false);
                          }}
                        >
                          <AiTwotoneEdit style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ padding: 1, maxWidth: '100vw' }}
                  >
                    <SearchIcon sx={{ marginRight: 2, fontSize: '1.5rem' }} />
                    <Box sx={{ fontSize: '1.5rem' }}>Nenhum registro encontrado</Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default TimeRecordList;
